/**
 * ============================================================================
 * ESCROW DASHBOARD COMPONENT - 100% Ready
 * ============================================================================
 * 
 * List of user's escrows with filtering
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useEscrow } from '../../hooks/useEscrow';
import { useToast } from '../../providers';
import { useI18n } from '../../providers';
import Card from '../common/Card';
import Spinner from '../common/Spinner';
import EmptyState from '../common/EmptyState';
import StatusBadge from '../common/StatusBadge';
import Tabs from '../common/Tabs';
import Button from '../common/Button';

/**
 * Escrow Dashboard Component
 * @returns {JSX.Element}
 */
const EscrowDashboard = () => {
  const navigate = useNavigate();
  const { listUserEscrows, escrows, loading } = useEscrow();
  const { showError } = useToast();
  const { formatCurrency, formatDate, language } = useI18n();
  const [activeFilter, setActiveFilter] = useState('all');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        await listUserEscrows({ status: activeFilter === 'all' ? undefined : activeFilter });
      } catch (error) {
        showError(
          error.message || (language === 'fr' ? 'Échec du chargement des escrows' : 'Failed to load escrows')
        );
        console.error('Load escrows error:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [activeFilter, listUserEscrows, showError, language]);

  const getStatusVariant = (status) => {
    const variants = {
      pending: 'warning',
      funded: 'success',
      frozen: 'error',
      released: 'success',
      refunded: 'info',
      partial: 'info',
      cancelled: 'default',
    };
    return variants[status] || 'default';
  };

  const getStatusIcon = (status) => {
    const icons = {
      pending: '⏳',
      funded: '🔒',
      frozen: '❄️',
      released: '✅',
      refunded: '↩️',
      partial: '📊',
      cancelled: '❌',
    };
    return icons[status] || '📦';
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'from-yellow-500 to-orange-500',
      funded: 'from-blue-500 to-cyan-500',
      frozen: 'from-red-500 to-pink-500',
      released: 'from-green-500 to-emerald-500',
      refunded: 'from-purple-500 to-pink-500',
      partial: 'from-indigo-500 to-purple-500',
      cancelled: 'from-gray-500 to-slate-500',
    };
    return colors[status] || 'from-gray-500 to-slate-500';
  };

  const tabs = [
    { id: 'all', label: language === 'fr' ? 'Tout' : 'All', count: escrows?.length || 0 },
    {
      id: 'funded',
      label: language === 'fr' ? 'Financés' : 'Funded',
      count: escrows?.filter((e) => e.status === 'funded').length || 0,
    },
    {
      id: 'frozen',
      label: language === 'fr' ? 'Gelés' : 'Frozen',
      count: escrows?.filter((e) => e.status === 'frozen').length || 0,
    },
    {
      id: 'released',
      label: language === 'fr' ? 'Libérés' : 'Released',
      count: escrows?.filter((e) => e.status === 'released').length || 0,
    },
  ];

  const filteredEscrows =
    activeFilter === 'all'
      ? escrows || []
      : (escrows || []).filter((e) => e.status === activeFilter);

  if (isLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-green-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8 animate-fadeInUp">
          <h1 className="text-4xl font-black bg-gradient-to-r from-blue-600 to-green-600 bg-clip-text text-transparent mb-2">
            {language === 'fr' ? 'Tableau de bord Escrow' : 'Escrow Dashboard'}
          </h1>
          <p className="text-gray-600 dark:text-gray-400 text-lg">
            {language === 'fr'
              ? 'Gérez tous vos comptes escrow'
              : 'Manage all your escrow accounts'}
          </p>
        </div>

        {/* Tabs */}
        <Card variant="glass" className="mb-6 animate-fadeInUp" style={{ animationDelay: '0.1s' }}>
          <Tabs tabs={tabs} activeTab={activeFilter} onTabChange={setActiveFilter} variant="pills" />
        </Card>

        {/* Escrows List */}
        {filteredEscrows.length > 0 ? (
          <div className="space-y-4">
            {filteredEscrows.map((escrow, index) => (
              <Card
                key={escrow.id}
                variant="glass"
                hover
                clickable
                onClick={() => navigate(`/escrows/${escrow.id}`)}
                className="animate-fadeInUp"
                style={{ animationDelay: `${0.2 + index * 0.05}s` }}
              >
                <div className="flex items-center gap-4">
                  <div
                    className={`w-12 h-12 rounded-xl bg-gradient-to-br ${getStatusColor(
                      escrow.status
                    )} flex items-center justify-center text-2xl shadow-lg flex-shrink-0`}
                  >
                    {getStatusIcon(escrow.status)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-black text-gray-900 dark:text-white">
                        {escrow.transaction?.title || escrow.id}
                      </h3>
                      <StatusBadge variant={getStatusVariant(escrow.status)} size="sm">
                        {escrow.status?.toUpperCase()}
                      </StatusBadge>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
                      <span>
                        {language === 'fr' ? 'Montant' : 'Amount'}:{' '}
                        {formatCurrency(escrow.amount || 0, escrow.currency || 'XAF')}
                      </span>
                      <span>•</span>
                      <span>
                        {language === 'fr' ? 'Créé le' : 'Created'}:{' '}
                        {escrow.createdAt && formatDate(escrow.createdAt)}
                      </span>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/escrows/${escrow.id}`);
                    }}
                    className="flex-shrink-0"
                  >
                    {language === 'fr' ? 'Voir' : 'View'} →
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        ) : (
          <Card variant="glass" className="p-12">
            <EmptyState
              icon="🔒"
              title={language === 'fr' ? 'Aucun escrow' : 'No escrows found'}
              description={
                language === 'fr'
                  ? 'Vous n\'avez pas encore d\'escrows'
                  : "You don't have any escrows yet"
              }
            />
          </Card>
        )}
      </div>
    </div>
  );
};

export default EscrowDashboard;
